import { cloneDeep, isEqual, get as lodashGet, merge, omit } from 'lodash'

import { ServicesNestJS } from '@sigma-frontend-libs/commons'

import { generateServices, globalErrorHandler, paramsMapByGeneration, permissionPresets, projectName, states } from '@/utils'

import visitedFavorites from '../visitedFavorites'
import render from './render'

/**
 * Собирает миксин шаблона сервиса.
 *
 * Предел постраничного вывода читается из localStorage. Ошибка разбора
 * испорченного значения подавляется: в этом случае используется значение
 * по умолчанию.
 *
 * @param {object} options Опции шаблона сервиса.
 * @returns {object} Миксин страницы сервиса.
 */
export function serviceTemplate(options = {}) {
  const { serviceName, inputFilter, outputFilter } = options

  const serviceNameViaPoint = serviceName.split('/').join('.')
  options.serviceNameViaPoint = serviceNameViaPoint

  const getCheckPermissionParams = method => {
    const permission = [
      'advanced',
      lodashGet(options, `permission.${method}.serviceName`, serviceNameViaPoint),
      lodashGet(options, `permission.${method}.method`, method)
    ].join('.')

    return [ permission, lodashGet(options, `permission.${method}.accept`, permissionPresets.meUp) ]
  }

  const defaultPaginationLimit = 20
  let limitFromLocalStorage = defaultPaginationLimit
  try {
    limitFromLocalStorage = JSON.parse(window.localStorage.getItem(`${projectName}:pagination:limit:${serviceNameViaPoint}`))
  } catch (error) {
  }

  options.views = options.views || [ 'panels' ]
  options.view = options.view || options.views[0]
  options.limit = options.limit || limitFromLocalStorage || defaultPaginationLimit
  options.width = options.width || 400
  options.dialog = options.dialog || {
    create: {},
    remove: {}
  }
  options.show = options.show || {
    after: {
      create: false
    }
  }
  options.backendGeneration = options.backendGeneration || 'legacy'

  const hasDefaults = {
    clone: true,
    copy: {
      title: true,
      id: true
    },
    search: true,
    filter: true,
    order: true,
    view: true,
    source: false,
    support: false,
    tabs: true
  }
  options.has = merge(hasDefaults, options.has || {})

  options.clone = merge({ replacements: {} }, options.replacements || {})

  options.errorProcessor = options.errorProcessor || undefined
  options.beforeCreated = options.beforeCreated || (() => {})
  options.mounted = options.mounted || (() => {})

  const mergeLegacyParams = params => {
    const result = {}
    if (!Object.values(ServicesNestJS).includes(serviceName)) {
      result.params = {
        query: {
          $scope: [ 'full' ]
        }
      }
    }

    return merge(result, params)
  }

  const find = () => {
    if (options.find === false) {
      return false
    }

    return merge({
      defaultFilter: { [paramsMapByGeneration[options.backendGeneration].search]: undefined },
      defaultPagination: { limit: options.limit }
    }, options.find)
  }
  const get = () => {
    if (options.get === false) {
      return false
    }

    return mergeLegacyParams(options.get)
  }
  const update = () => {
    if (options.update === false) {
      return false
    }

    return mergeLegacyParams(options.update)
  }
  const create = () => {
    if (options.create === false) {
      return false
    }

    return options.create
  }
  const remove = () => {
    if (options.remove === false) {
      return false
    }

    return options.remove
  }

  const mixins = [
    generateServices([
      {
        name: serviceName,
        path: options.path,
        backendGeneration: options.backendGeneration,

        inputFilter,
        outputFilter,

        errorProcessor: options.errorProcessor,

        find: find(),
        get: get(),
        update: update(),
        create: create(),
        remove: remove()
      }
    ])
  ]

  if (options.has.tabs) {
    mixins.push(visitedFavorites({ service: options.serviceName }))
  }

  return {
    name: 'Template',

    mixins,

    data() {
      return {
        view: options.view,

        showCreateDialog: false,
        showRemoveDialog: false,
        showSourceDialog: false,

        initialData: undefined,
        createdData: undefined,

        show: options.show
      }
    },

    computed: {
      canCreate() {
        return this.checkPermissions(...getCheckPermissionParams('create'))
      },
      canRemove() {
        return this.checkPermissions(...getCheckPermissionParams('remove'))
      },
      canUpdate() {
        return this.checkPermissions(...getCheckPermissionParams('update'))
      },
      canGet() {
        return this.checkPermissions(...getCheckPermissionParams('get'))
      },

      dataIsEqual() {
        return isEqual(
          JSON.stringify(cloneDeep(this.initialData)),
          JSON.stringify(cloneDeep(this.restData[options.serviceName].get.data))
        )
      },

      updateLoading() {
        return this.restData[options.serviceName].update.state === states.loading
      },
      updateDisabled() {
        return this.updateLoading
          || !this.restData[options.serviceName].update.isValid
          || !this.canUpdate
          || this.dataIsEqual
      },

      serviceNameViaPoint() {
        return serviceNameViaPoint
      },

      title() {
        const data = this.restData[options.serviceName].get.data
        if (data) {
          const result = data.title || lodashGet(data, options.titlePath)
          if (result) {
            return result
          }
        }

        return this.getTranslate(`${serviceNameViaPoint}.title`)
      },
      $title() {
        const data = this.restData[options.serviceName].get.data
        if (data) {
          return data.title || lodashGet(data, options.titlePath)
        }
      },
      titleURLSlack() {
        if (this.title) {
          return `[${this.title}](${window.location.href})`
        }
      },

      /**
       * Признак заданных в опциях шаблона полей сортировки `orderFields`.
       *
       * @returns {boolean} `true`, если `orderFields` - непустой массив.
       */
      hasOrderFields() {
        return Array.isArray(options.orderFields) && options.orderFields.length > 0
      },
      /**
       * Признак вывода кнопки сортировки в шапке списка.
       * Кнопка выводится, если включено `has.order` и заданы `orderFields`.
       *
       * @returns {boolean} `true`, если кнопка сортировки выводится.
       */
      hasOrder() {
        return options.has.order && this.hasOrderFields
      },

      headerFieldsGridTemplateColumns() {
        const result = []
        if (this.viewport.breakpoint.mdUp) {
          if (options.has.search) {
            result.push('250px')
          }
          if (options.has.filter) {
            result.push('1fr')
          }
        } else if (this.viewport.breakpoint.smUp) {
          if (options.has.search) {
            result.push('1fr')
          }
          if (options.has.filter) {
            result.push('1fr')
          }
        } else {
          result.push('1fr')
        }

        return result.join(' ')
      }
    },

    watch: {
      $route: {
        handler() {
          this.getOrFind()
          this.watchRouteQuery()
        },
        deep: true
      }
    },

    beforeCreated() {
      options.beforeCreated.call(this)
    },

    async mounted() {
      await options.mounted.call(this)

      await this.getOrFind()
      await this.watchRouteQuery()

      if (options.has.tabs) {
        this.getVisited()
        this.getFavorites()
      }
    },

    methods: {
      async create() {
        if (this.canCreate) {
          if (this.restData[serviceName].create.isValid) {
            try {
              const response = await this.rest[serviceName].create(this.restData[serviceName].create.data)
              if (response) {
                this.createdData = cloneDeep(response)
                this.showCreateDialog = false
                this.show.after.create = true
              }
            } catch (error) {
              globalErrorHandler(error)
            }
          }
        }
      },

      async remove() {
        if (this.canRemove) {
          try {
            const response = await this.rest[serviceName].remove(this.$route.params.id)
            if (response) {
              this.showRemoveDialog = false
            }
          } catch (error) {
            globalErrorHandler(error)
          }
        }
      },

      async update() {
        if (this.canUpdate) {
          if (this.restData[serviceName].update.isValid) {
            try {
              const response = await this.rest[serviceName].update(this.restData[serviceName].get.data)
              if (response) {
                this.initialData = cloneDeep(response)
              }
            } catch (error) {
              globalErrorHandler(error)
            }
          }
        }
      },

      async getOrFind() {
        if (this.canGet) {
          if (this.$route.params.id) {
            const response = await this.rest[serviceName].get(this.$route.params.id)
            if (response) {
              this.initialData = cloneDeep(response)
            } else {
              this.initialData = undefined
            }
          } else {
            await this.rest[serviceName].find()
          }
        }
      },

      async watchRouteQuery() {
        const create = lodashGet(this.$route, 'query.create')
        if (create) {
          this.showCreateDialog = true
        }
      },

      async clone() {
        const omittedFields = [ 'id', 'createdAt', 'updatedAt', '$formattedCreatedAt', '$formattedUpdatedAt', 'errors' ]
        const cloneData = omit(cloneDeep(this.restData[serviceName].get.data), omittedFields)

        const regex = /\((\d+)\)$/
        const titlePath = options.titlePath || 'title'
        const match = cloneData[titlePath].match(regex)

        if (match) {
          const index = parseInt(match[1])
          cloneData[titlePath] = cloneData[titlePath].replace(regex, `(${index + 1})`)
        } else {
          cloneData[titlePath] = `${cloneData[titlePath]} (1)`
        }

        return await this.rest[serviceName].create(merge(cloneData, options.replacements))
      },

      copy(key, value) {
        if (options.has.copy[key]) {
          navigator.clipboard.writeText(value)
          this.addSnackbar({
            text: this.getTranslate('misc.copied'),
            type: 'success'
          })
        }
      }
    },

    render(h) {
      return render.call(this, h, options)
    }
  }
}
